import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CrudPage } from "@/components/CrudPage";
import { yen, pct } from "@/lib/format";
import { useLookups } from "@/context/LookupContext";
import { EXPENSE_CATEGORY } from "@/lib/constants";
import { Loader2, TrendingUp, TrendingDown, Wallet, Percent } from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Legend, Tooltip,
} from "recharts";

const CAT_OPTS = Object.entries(EXPENSE_CATEGORY).map(([value, v]) => ({ value, label: v.label }));
const COLORS = ["#FF5A00", "#111111", "#F59E0B", "#3B82F6", "#10B981", "#8B5CF6", "#94A3B8"];

const expenseColumns = [
  { key: "date", label: "日付", render: (r) => r.date },
  { key: "property_id", label: "物件", render: (r, h) => (r.property_id ? h.nameOf("properties", r.property_id) : "全体") },
  { key: "category", label: "科目", render: (r) => EXPENSE_CATEGORY[r.category]?.label || r.category },
  { key: "amount", label: "金額", render: (r, h) => h.yen(r.amount) },
  { key: "note", label: "備考", render: (r) => <span className="text-muted-foreground">{r.note || "-"}</span> },
];

const expenseFields = [
  { name: "date", label: "日付 (YYYY-MM-DD)", type: "date", required: true },
  { name: "property_id", label: "物件", type: "select", lookup: "properties" },
  { name: "category", label: "科目", type: "select", options: CAT_OPTS, default: "other", required: true },
  { name: "amount", label: "金額", type: "currency", required: true },
  { name: "note", label: "備考", type: "textarea" },
];

function Metric({ label, value, tone, icon: Icon }) {
  const toneClass = { green: "text-emerald-600", red: "text-red-600", brand: "text-brand", default: "text-[#0A0A0A]" }[tone || "default"];
  return (
    <div className="bg-white border border-border rounded-sm p-4 md:p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-300" />}
      </div>
      <div className={`font-display text-2xl md:text-3xl font-light ${toneClass}`}>{value}</div>
    </div>
  );
}

export default function Finance() {
  const { lookups } = useLookups();
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [propertyId, setPropertyId] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year });
      if (propertyId) params.append("property_id", propertyId);
      const { data } = await api.get(`/finance/summary?${params}`);
      setSummary(data);
    } catch {} finally { setLoading(false); }
  }, [year, propertyId]);

  useEffect(() => { load(); }, [load]);

  const props = propertyId ? lookups.properties.filter((p) => p.id === propertyId) : lookups.properties;
  const totalValue = props.reduce((s, p) => s + Number(p.current_value || 0), 0);
  const grossYield = totalValue ? (summary?.income.rent + summary?.income.parking) / totalValue * 100 : 0;
  const netYield = totalValue ? (summary?.noi || 0) / totalValue * 100 : 0;

  const expenseData = summary ? [
    { name: "修繕", value: summary.expense.repair },
    { name: "ローン", value: summary.expense.loan },
    ...Object.entries(summary.expense.categories || {}).map(([k, v]) => ({ name: EXPENSE_CATEGORY[k]?.label || k, value: v })),
  ].filter((x) => x.value > 0) : [];

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="収支管理"
        subtitle="物件単位・会社単位の収支サマリー"
        actions={
          <div className="flex items-center gap-2">
            <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
              className="h-10 px-3 rounded-sm border border-input bg-white text-sm" data-testid="finance-property">
              <option value="">会社全体</option>
              {lookups.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(e.target.value)}
              className="h-10 px-3 rounded-sm border border-input bg-white text-sm" data-testid="finance-year">
              {[0, 1, 2].map((d) => { const y = new Date().getFullYear() - d; return <option key={y} value={y}>{y}年</option>; })}
            </select>
          </div>
        }
      />

      {loading || !summary ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6">
            <Metric label="総収入" value={yen(summary.income.total)} tone="green" icon={TrendingUp} />
            <Metric label="総支出" value={yen(summary.expense.total)} tone="red" icon={TrendingDown} />
            <Metric label="NOI" value={yen(summary.noi)} tone="brand" icon={Wallet} />
            <Metric label="キャッシュフロー" value={yen(summary.cashflow)} tone={summary.cashflow >= 0 ? "green" : "red"} icon={Wallet} />
            <Metric label="表面利回り" value={pct(grossYield.toFixed(2))} tone="default" icon={Percent} />
            <Metric label="実質利回り" value={pct(netYield.toFixed(2))} tone="default" icon={Percent} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-border rounded-sm p-5">
              <h3 className="text-sm font-medium mb-4">収入内訳（{year}年）</h3>
              {[
                ["家賃", summary.income.rent], ["共益費", summary.income.common],
                ["駐車場", summary.income.parking], ["その他", summary.income.other],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-2 border-b last:border-0">
                  <span className="text-sm text-muted-foreground">{k}</span>
                  <span className="text-sm font-medium">{yen(v)}</span>
                </div>
              ))}
              <div className="flex justify-between py-2 mt-1 border-t-2 border-[#111]">
                <span className="text-sm font-medium">合計</span>
                <span className="text-sm font-display text-emerald-600">{yen(summary.income.total)}</span>
              </div>
            </div>
            <div className="bg-white border border-border rounded-sm p-5">
              <h3 className="text-sm font-medium mb-4">支出内訳（{year}年）</h3>
              {expenseData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">支出データがありません</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={expenseData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                      {expenseData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => yen(v)} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="mt-2">
            <CrudPage
              title="経費管理"
              endpoint="/finance/expenses"
              resource="expenses"
              columns={expenseColumns}
              fields={expenseFields}
              testid="expenses"
              addLabel="経費を登録"
              searchKeys={["category", "note"]}
            />
          </div>
        </>
      )}
    </div>
  );
}
